import { useAtom } from "jotai";
import classNames from "classnames/dedupe";
import { useBreakpoint } from "@simplybusiness/mobius";
import type { WithMetadata } from "../../../pages";
import { NoQuotesCard } from "../NoQuotesCard";
import { NoQuotesReferral } from "../NoQuotesReferral";
import { PartialQuotesReferral } from "./PartialQuotesReferral";
import { QuoteCard } from "./QuoteCard";
import { Section } from "../Section";
import { sortByPremium, sortByInstallmentAmount } from "../sortByPrice";
import type { Quote } from "../types";
import { Unquotable } from "../Unquotable";
import { useMutationStatus } from "../../../hooks/useMutationStatus";
import { QuotesSkeleton } from "../QuotesSkeleton";
import { qcpLayoutToggleAtom, paymentToggleAtom } from "../../../store";
import { customBreakpoints } from "../../../constants/breakpoints";
import { useState, useEffect } from "react";
import { MONTHLY } from "../../../constants/constants";

export type QuoteCardsProps = {
  quotes?: Quote[] | null;
  // UK specific
  contactPhoneNumber?: string;
  primaryDetailSelected?: string;
  // Referral specific
  customerName?: string;
  referenceNumber?: string;
  coverOptionsUrl?: string;
  view?: string;
};

const useHydrated = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  return hydrated;
};

export const QuoteCards = (props: WithMetadata<QuoteCardsProps>) => {
  const { quotes, ...otherProps } = props;
  const isToggling = useMutationStatus("toggleCovers");
  const { componentName, variant, customerName, coverOptionsUrl } = otherProps;
  const [paymentPeriod] = useAtom(paymentToggleAtom);
  const unquotedQuotes: Quote[] = [];
  const quotedQuotes = [];
  (quotes || []).forEach(quote => {
    if (quote.status === "quoted") {
      quotedQuotes.push(quote);
    } else {
      unquotedQuotes.push(quote);
    }
  });

  const eligibleForReferralReasons = variant === "referral-reasons";
  const withoutQdpDrawer = variant === "without-qdp-drawer";
  const variantWithoutQdpDrawer =
    withoutQdpDrawer || eligibleForReferralReasons;

  const quotedQuotesCount = quotedQuotes.length;
  const showReferral = withoutQdpDrawer && quotedQuotesCount === 0;

  const showPartialQuotesReferral =
    eligibleForReferralReasons &&
    customerName !== undefined &&
    coverOptionsUrl !== undefined;
  const showUnquotable = variant === "unsupported-trade-referral";
  const hideNoQuoteCard = showPartialQuotesReferral;
  const { up } = useBreakpoint(customBreakpoints);
  const isDesktop = up("lg");
  const hydrated = useHydrated();
  const [layout] = useAtom(qcpLayoutToggleAtom);

  if (!hydrated) {
    return <QuotesSkeleton {...props} />;
  }
  const isGridLayout = layout === "grid";

  const classes = classNames(
    "mobius-quote-cards__grid",
    "--is-grid-view-treatment",
    {
      "--is-horizontal":
        (isGridLayout && !isDesktop) ||
        (!isGridLayout && variantWithoutQdpDrawer),
      "--is-grid": isGridLayout && isDesktop,
    },
  );

  if (isToggling) {
    return <QuotesSkeleton {...props} />;
  }

  if (showUnquotable) {
    return (
      <Unquotable
        contactPhoneNumber={otherProps.contactPhoneNumber}
        referenceNumber={otherProps.referenceNumber}
      />
    );
  }

  if (showReferral) {
    return <NoQuotesReferral quotes={quotes} {...otherProps} />;
  }

  const isQuotesCountMultipleOfThree = !!quotes && quotes.length % 3 === 0;
  let partialQuotesSignpostDisplay = null;
  if (showPartialQuotesReferral) {
    partialQuotesSignpostDisplay = isQuotesCountMultipleOfThree
      ? "banner"
      : "grid";
  }

  const referredQuotes: Quote[] = [];
  const nonReferredQuotes: Quote[] = [];
  (quotes || []).forEach(q => {
    if (q.status === "referred") {
      referredQuotes.push(q);
    } else {
      nonReferredQuotes.push(q);
    }
  });

  const sortedQuotes = nonReferredQuotes.sort(
    paymentPeriod === MONTHLY ? sortByInstallmentAmount : sortByPremium,
  );
  const showNoQuotesCard = sortedQuotes.length === 0;

  const renderQuoteCards = (quotesToRender: Quote[]) =>
    quotesToRender.map(quote => (
      <QuoteCard
        key={quote.id}
        quote={quote}
        variant={variant}
        hideNoQuoteCard={hideNoQuoteCard}
      />
    ));

  return (
    <Section
      {...(componentName ? { "data-component": componentName } : {})}
      {...(variant ? { "data-variant": variant } : {})}
    >
      <div role="list" className={classes} data-testid="quote-cards-list">
        {showNoQuotesCard ? (
          <NoQuotesCard />
        ) : (
          <>
            {renderQuoteCards(sortedQuotes)}
            {!isGridLayout &&
              referredQuotes.length > 0 &&
              renderQuoteCards(referredQuotes)}
            {partialQuotesSignpostDisplay === "grid" && (
              <PartialQuotesReferral
                quotes={unquotedQuotes}
                customerName={customerName || ""}
                coverOptionsUrl={coverOptionsUrl || ""}
                view={isGridLayout ? "grid" : "banner"}
              />
            )}
          </>
        )}
      </div>
      {/*
        This section renders referred quotes in grid layout, outside the main list,
        to ensure correct visual grouping and separation.
      */}
      {isGridLayout &&
        !showPartialQuotesReferral &&
        referredQuotes.length > 0 && (
          <div className={classes}>{renderQuoteCards(referredQuotes)}</div>
        )}
      {partialQuotesSignpostDisplay === "banner" && (
        <PartialQuotesReferral
          quotes={unquotedQuotes}
          customerName={customerName || ""}
          coverOptionsUrl={coverOptionsUrl || ""}
          view="banner"
        />
      )}
    </Section>
  );
};
